import React from 'react';
import { SubcontractorProject, SubcontractorTask } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { formatDateOnly } from '@utils/formatDate';
import { formatMoney, t } from '../utils/format';
import { HealthPill } from './HealthPill';
import { getTranslatedTaskStatusLabel } from '@utils/options';

interface WorkTabProps {
  projects: SubcontractorProject[];
  onOpenTask: (taskId: string) => void;
}

const TaskLateLeft: React.FC<{ task: SubcontractorTask }> = ({ task }) => {
  const tSub = useTranslation('subcontractors');
  if (task.completedAt) {
    return <span className="text-gray-500 dark:text-gray-400">{t(tSub?.doneOn, 'done {date}', { date: formatDateOnly(task.completedAt) })}</span>;
  }
  if (task.daysLate != null) {
    return <span className="text-red-600 dark:text-red-400">{t(tSub?.daysLate, '{d} d late', { d: task.daysLate })}</span>;
  }
  if (task.daysLeft != null) {
    return <span className="text-gray-500 dark:text-gray-400">{t(tSub?.daysLeft, '{d} d left', { d: task.daysLeft })}</span>;
  }
  return <span>—</span>;
};

const TaskOrigin: React.FC<{ task: SubcontractorTask }> = ({ task }) => {
  const tSub = useTranslation('subcontractors');
  if (task.origin.type === 'BID') {
    return <span>{t(tSub?.originBid, 'Bid · {amount}', { amount: formatMoney(task.origin.amount) })}</span>;
  }
  return <span>{tSub?.originDirect || 'Direct'}</span>;
};

const InventoryLines: React.FC<{ task: SubcontractorTask }> = ({ task }) => {
  const tSub = useTranslation('subcontractors');
  const [open, setOpen] = React.useState(false);
  if (task.materials.length === 0 && task.equipment.length === 0) return null;

  return (
    <tr>
      <td colSpan={7} className="px-4 pb-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          className="text-xs text-indigo-700 dark:text-indigo-300 hover:underline">
          {open ? '▾' : '▸'}{' '}
          {t(tSub?.inventoryLine, '{m} materials · {e} equipment', {
            m: task.materials.length,
            e: task.equipment.length,
          })}
        </button>
        {open && (
          <div className="mt-1 pl-4 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
            {task.materials.map((line, idx) => (
              <div key={`m-${idx}`}>
                {line.name}
                {line.quantity != null ? ` × ${line.quantity}${line.unit ? ` ${line.unit}` : ''}` : ''}
                {!line.isMine && line.pricePerUnit == null ? ` — ${tSub?.costHiddenNote || "Sub's cost basis hidden"}` : ''}
              </div>
            ))}
            {task.equipment.map((line, idx) => (
              <div key={`e-${idx}`}>
                {line.name}
                {line.estimatedHours != null ? ` · ${line.estimatedHours} h` : ''}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
};

const ProjectWorkTable: React.FC<{ project: SubcontractorProject; onOpenTask: (taskId: string) => void }> = ({
  project,
  onOpenTask,
}) => {
  const tSub = useTranslation('subcontractors');
  // Sprint 28 change request (global sweep, defect fix). `task.status` was rendered raw
  // (`TaskStatus` enum) — reuses `getTranslatedTaskStatusLabel` (`@utils/options`), the same
  // helper `ActivityTaskStatus` already uses, instead of a second status map.
  const tTaskDetail = useTranslation('taskDetail');
  const countableTasks = project.tasks.filter((task) => task.countable);
  const cancelledTasks = project.tasks.filter((task) => !task.countable);
  const [showCancelled, setShowCancelled] = React.useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg ring-1 ring-gray-900/5 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700">
        <span className="font-semibold text-gray-800 dark:text-gray-100">{project.projectName}</span>
        <HealthPill health={project.health} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {Math.round(project.completion * 100)}% · {countableTasks.length} tasks
        </span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <th className="px-4 py-2">{tSub?.colTask || 'Task'}</th>
            <th className="px-4 py-2">{tSub?.colStatus || 'Status'}</th>
            <th className="px-4 py-2">{tSub?.colStart || 'Start'}</th>
            <th className="px-4 py-2">{tSub?.colEnd || 'End'}</th>
            <th className="px-4 py-2">{tSub?.colLateLeft || 'Late/left'}</th>
            <th className="px-4 py-2 text-right">Agreed</th>
            <th className="px-4 py-2">{tSub?.colOrigin || 'Origin'}</th>
          </tr>
        </thead>
        <tbody>
          {countableTasks.map((task) => (
            <React.Fragment key={task.taskId}>
              <tr
                onClick={() => onOpenTask(task.taskId)}
                className="border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 text-gray-800 dark:text-gray-100">{task.name}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{getTranslatedTaskStatusLabel(task.status, tTaskDetail)}</td>
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                  {task.startTime ? formatDateOnly(task.startTime) : '—'}
                </td>
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                  {task.endTime ? formatDateOnly(task.endTime) : '—'}
                </td>
                <td className="px-4 py-2">
                  <TaskLateLeft task={task} />
                </td>
                <td className="px-4 py-2 text-right">{formatMoney(task.budget, tSub?.notSet || 'Not set')}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                  <TaskOrigin task={task} />
                </td>
              </tr>
              <InventoryLines task={task} />
            </React.Fragment>
          ))}
          {cancelledTasks.length > 0 && (
            <>
              <tr>
                <td colSpan={7} className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setShowCancelled((s) => !s)}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline">
                    {showCancelled ? '▾' : '▸'}{' '}
                    {t(tSub?.cancelledGroup, '{n} cancelled', { n: cancelledTasks.length })}
                  </button>
                </td>
              </tr>
              {showCancelled &&
                cancelledTasks.map((task) => (
                  <tr key={task.taskId} className="border-t border-gray-100 dark:border-gray-700 opacity-60">
                    <td className="px-4 py-2">{task.name}</td>
                    <td className="px-4 py-2">{getTranslatedTaskStatusLabel(task.status, tTaskDetail)}</td>
                    <td className="px-4 py-2">{task.startTime ? formatDateOnly(task.startTime) : '—'}</td>
                    <td className="px-4 py-2">{task.endTime ? formatDateOnly(task.endTime) : '—'}</td>
                    <td className="px-4 py-2">—</td>
                    <td className="px-4 py-2 text-right">{formatMoney(task.budget, tSub?.notSet || 'Not set')}</td>
                    <td className="px-4 py-2">
                      <TaskOrigin task={task} />
                    </td>
                  </tr>
                ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const WorkTab: React.FC<WorkTabProps> = ({ projects, onOpenTask }) => {
  return (
    <div className="space-y-4 pb-12">
      {projects.map((project) => (
        <ProjectWorkTable key={project.projectId} project={project} onOpenTask={onOpenTask} />
      ))}
    </div>
  );
};

export default WorkTab;
